/* eslint-disable tailwindcss/no-arbitrary-value */
import React from 'react';
import { Typography, Link, RatingMeter } from '@one-thd/sui-atomic-components';
import {
  shape as shapeProp,
  number as propTypeNumber,
  string as propTypeString,
  func as propTypeFunc,
  bool as propTypeBool,
  arrayOf as arrayOfProp
} from 'prop-types';

import {
  getTextOnlyFuelType,
  getVariantGroup,
  isProductInVariantGroup,
  getApplianceType,
  compareString
} from '../../utils/selectable-variant-utils';
import { ProductButtonText } from './ProductButtonText';
import { InfoDrawer } from './infoDrawer/InfoDrawer';
import { KITCHEN_FUEL_TYPE, DRYER_FUEL_TYPE, DRYER } from '../../constants/index';
import { BundleTemplateLayout } from './BundleTemplateLayout';
import { onClickProductAccordionLink, accordionToggle, isProductActive } from '../../utils/product-bundle-utils';

export const ProductListDetails = (props) => {
  const {
    product,
    openQuickView,
    isMobile,
    accordionProps,
    setIsRatingOpen,
    selectableVariants,
    swapProducts,
    templates,
    selectableVariantProducts
  } = props;
  const {
    quickView,
    productListFuelType,
    productListModelNumber,
    productListRating,
    productListViewSpecificationLink,
    showVariantDrawerInfoIcon,
    productLabelScrollableLink,
    brandNameScrollableLink,
    showApplianceType,
    disableAccordionInteractionsWhenUnavailable
  } = product?.features ?? {};

  const isActive = isProductActive(product);
  const { productSpecificationAccordionName, scrollStickyHeaderHeight, productDetailsAccordionName } = accordionProps;
  const applianceType = getApplianceType(product);
  const variantNameForFuelType = compareString(applianceType, DRYER) ? DRYER_FUEL_TYPE : KITCHEN_FUEL_TYPE;
  const textOnlyFuelType = productListFuelType
    && getTextOnlyFuelType(product, variantNameForFuelType, selectableVariants);
  const { variantName, variants } = getVariantGroup(selectableVariants);
  const isInVariantGroup = isProductInVariantGroup({ productId: product?.itemId, variants });

  const showInfoDrawer = showVariantDrawerInfoIcon && variantName && isInVariantGroup;

  const disableScrollToAccordions = !isActive && disableAccordionInteractionsWhenUnavailable;

  const disableScrollToSpecifications = !productListViewSpecificationLink || disableScrollToAccordions;

  const handleOpenAccordion = React.useCallback(
    (event, accordionName) => {
      onClickProductAccordionLink({
        event,
        product,
        accordionName,
        isMobile,
        scrollStickyHeaderHeight
      });
    },
    [product, isMobile, scrollStickyHeaderHeight]
  );

  return (
    <div className="sui-w-full">
      {
        quickView && (
          <>
            <div className="sui-flex sui-gap-x-1">
              <Link
                variant="body-base"
                underline="hover"
                onClick={() => openQuickView(product)}
                data-testid="product-list-brand-label"
              >
                <b>{product?.identifiers?.brandName}</b>
                {showApplianceType && (
                  <span className="sui-ml-1 sui-font-bold">
                    {applianceType}
                  </span>
                )}
              </Link>
              {showInfoDrawer && <InfoDrawer variantNames={[variantName]} />}
            </div>
            <Link
              variant="body-base"
              underline="hover"
              onClick={() => openQuickView(product)}
              data-testid="product-list-product-label"
            >
              {product?.identifiers?.productLabel}
            </Link>
          </>
        )
      }
      {brandNameScrollableLink && (
        <div className="sui-flex sui-gap-x-1 sui-items-center">
          <ProductButtonText
            onClick={
              disableScrollToAccordions
                ? null
                : (event) => {
                  handleOpenAccordion(event, productDetailsAccordionName);
                  accordionToggle({ accordionName: productSpecificationAccordionName });
                }
            }
            underline="hover"
          >
            <Typography
              variant="body-base"
              weight="bold"
              data-testid="product-list-brand-label"
              align="left"
              height="normal"
            >
              {product?.identifiers?.brandName}
            </Typography>
          </ProductButtonText>
          {showInfoDrawer && <InfoDrawer variantNames={[variantName]} />}
        </div>
      )}
      {
        productLabelScrollableLink && (
          <ProductButtonText
            onClick={
              disableScrollToAccordions
                ? null
                : (event) => {
                  handleOpenAccordion(event, productDetailsAccordionName);
                  accordionToggle({ accordionName: productSpecificationAccordionName });
                }
            }
            underline="hover"
          >
            <Typography
              lineClamp="2"
              align="left"
              variant="body-base"
              data-testid="product-list-product-label"
              height="normal"
            >
              {product?.identifiers?.productLabel}
            </Typography>
          </ProductButtonText>
        )
      }
      {
        product?.quantity > 1 && (
          <Typography variant="body-base" data-testid="product-list-quantity">
            Qty: {product?.quantity}
          </Typography>
        )
      }
      {
        productListModelNumber && product?.identifiers?.modelNumber && (
          <Typography variant="body-xs" color="subtle">
            Model # {product.identifiers.modelNumber}
          </Typography>
        )
      }
      {
        textOnlyFuelType && (
          <Typography variant="body-xs">
            <p className="sui-text-base sui-pt-3"> Fuel Type -
              <span className="sui-font-bold sui-pl-1">
                {textOnlyFuelType}
              </span>
            </p>
          </Typography>
        )
      }
      {
        productListRating && product?.reviews?.ratingsReviews && (
          // eslint-disable-next-line tailwindcss/no-arbitrary-value
          <div className="sui-flex sui-pt-3 [&>*]:sui-gap-1">
            <RatingMeter
              data-testid="product-list-item-rating-reviews"
              className="hover:sui-cursor-pointer"
              value={parseFloat(product?.reviews?.ratingsReviews?.averageRating)}
              label={`(${product?.reviews?.ratingsReviews?.totalReviews})`}
              target="_blank"
              edge="start"
              RatingProps={{
                color: 'brand'
              }}
              onClick={() => {
                setIsRatingOpen(true);
                openQuickView(product);
              }}
            />
          </div>
        )
      }
      <BundleTemplateLayout
        selectableVariants={selectableVariants}
        templates={templates}
        product={product}
        onChange={swapProducts}
        selectableVariantProducts={selectableVariantProducts}
      />
      {
        disableScrollToSpecifications || (
          <div className="sui-pt-3">
            <ProductButtonText
              onClick={(event) => handleOpenAccordion(event, productSpecificationAccordionName)}
              underline="always"
            >
              View Item Specifications
            </ProductButtonText>
          </div>
        )
      }
    </div>
  );
};

ProductListDetails.propTypes = {
  product: shapeProp({}),
  openQuickView: propTypeFunc.isRequired,
  isMobile: propTypeBool,
  accordionProps: shapeProp({
    productSpecificationAccordionName: propTypeString,
    scrollStickyHeaderHeight: propTypeNumber,
    productDetailsAccordionName: propTypeString
  }).isRequired,
  setIsRatingOpen: propTypeFunc.isRequired,
  selectableVariants: shapeProp({}),
  swapProducts: propTypeFunc.isRequired,
  templates: shapeProp({
    [propTypeString]: propTypeString
  }).isRequired,
  selectableVariantProducts: arrayOfProp(shapeProp({ })).isRequired
};

ProductListDetails.defaultProps = {
  product: null,
  isMobile: false,
  selectableVariants: null
};